import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { HealthDotComponent } from '@/shared/ui/health-dot/health-dot.component';
import { status, StatusDescriptor } from '@/shared/ui/status.model';
import { deriveDeviceStatus } from '@/cloud/models/device-status';
import { DeviceCard, DevicesBlock } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';
import { timeAgo } from '../assistant-format';

interface LiveState {
    status?: number;
    lastCheckInUtc?: string;
}

/**
 * Device cards with a live status dot: each card subscribes to its device's topic, so a reboot shows the
 * dot go red and come back green without asking again.
 */
@Component({
    selector: 'app-assistant-device-cards',
    standalone: true,
    imports: [HealthDotComponent, TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (block().title) {
            <div class="ab-title">{{ block().title }}</div>
        }
        <div class="device-grid">
            @for (d of block().devices; track d.deviceId) {
                <article class="device-card" [class.is-offline]="dot(d).key === 'offline'">
                    <header>
                        <app-health-dot [status]="dot(d)" [pulse]="dot(d).key === 'online'" [label]="d.name" />
                        <button type="button" class="entity-link" (click)="openDevice(d)" [pTooltip]="'Open ' + d.name" tooltipPosition="top">{{ d.name }}</button>
                        <span class="state" [attr.data-status]="dot(d).key">{{ dot(d).label }}</span>
                    </header>
                    <div class="meta">
                        @if (d.locationName) {
                            <span><i class="pi pi-map-marker" aria-hidden="true"></i>{{ d.locationName }}</span>
                        }
                        @if (d.namespaceName && showTenant()) {
                            <span><i class="pi pi-building" aria-hidden="true"></i>{{ d.namespaceName }}</span>
                        }
                        @if (d.model) {
                            <span><i class="pi pi-microchip" aria-hidden="true"></i>{{ d.model }}</span>
                        }
                        <span><i class="pi pi-clock" aria-hidden="true"></i>{{ seen(d) }}</span>
                    </div>
                    <footer>
                        <button type="button" class="quick" (click)="go(d, 'logs')"><i class="pi pi-list" aria-hidden="true"></i>{{ t()('assistant.devices.viewLogs') }}</button>
                        <button type="button" class="quick" (click)="go(d, 'config')"><i class="pi pi-sliders-h" aria-hidden="true"></i>{{ t()('assistant.devices.openConfig') }}</button>
                        <button type="button" class="quick" (click)="ask(d)"><i class="pi pi-comment" aria-hidden="true"></i>{{ t()('assistant.devices.ask') }}</button>
                        @if (block().canAct) {
                            <button type="button" class="quick danger" [disabled]="proposing() === d.deviceId" (click)="propose(d, 'Reboot')"><i class="pi pi-power-off" aria-hidden="true"></i>{{ t()('assistant.devices.reboot') }}</button>
                        }
                    </footer>
                </article>
            }
        </div>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantDeviceCardsComponent implements OnInit {
    readonly block = input.required<DevicesBlock>();

    private api = inject(CloudApiService);
    private assistant = inject(AssistantService);
    private tenants = inject(TenantService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    readonly t = inject(TranslationService).t;

    private readonly live = signal<Record<string, LiveState>>({});
    private readonly now = signal(Date.now());
    readonly proposing = signal<string | null>(null);
    readonly showTenant = computed(() => this.assistant.scope() === 'serviceDesk');

    ngOnInit(): void {
        const subs: Subscription[] = [];
        for (const d of this.block().devices) {
            subs.push(
                this.api.subscribeToDevice(d.deviceId).subscribe((device) => {
                    if (!device) return;
                    this.live.update((map) => ({ ...map, [d.deviceId]: { status: device.status, lastCheckInUtc: device.lastCheckInUtc } }));
                })
            );
        }
        // Offline is "no check-in for five minutes": re-evaluate as time passes.
        const timer = setInterval(() => this.now.set(Date.now()), 30_000);
        this.destroyRef.onDestroy(() => {
            clearInterval(timer);
            subs.forEach((s) => s.unsubscribe());
        });
    }

    dot(d: DeviceCard): StatusDescriptor {
        const live = this.live()[d.deviceId];
        if (live?.status !== undefined) return deriveDeviceStatus({ status: live.status, lastCheckInUtc: live.lastCheckInUtc ?? d.lastCheckInUtc }, this.now());
        if (d.offline) return status('offline');
        if (d.status === 'Active') return status('online');
        if (d.status === 'Error') return status('warning', 'Error');
        if (d.status === 'Pending') return status('pending', 'Awaiting Adoption');
        return status('missing', d.status || 'Unknown');
    }

    seen(d: DeviceCard): string {
        const last = this.live()[d.deviceId]?.lastCheckInUtc ?? d.lastCheckInUtc;
        const ago = timeAgo(last, this.now());
        return ago ? fill(this.t()('assistant.devices.lastSeen'), { time: ago }) : this.t()('assistant.devices.neverSeen');
    }

    openDevice(d: DeviceCard): void {
        this.go(d, null);
    }

    go(d: DeviceCard, tab: 'logs' | 'config' | null): void {
        if (d.namespaceId && d.namespaceId !== this.tenants.selectedTenantId()) this.tenants.select(d.namespaceId);
        this.router.navigate(tab ? ['/device', d.deviceId, tab] : ['/device', d.deviceId]);
    }

    ask(d: DeviceCard): void {
        this.assistant.send(`Tell me about ${d.name}: is it healthy, and has anything changed recently?`, [{ type: 'device', id: d.deviceId, label: d.name }]);
    }

    async propose(d: DeviceCard, action: 'Reboot' | 'Restart'): Promise<void> {
        this.proposing.set(d.deviceId);
        try {
            await this.assistant.proposeAction(d.deviceId, action, d.namespaceId);
        } catch {
            this.assistant.applyEvent({ type: 'error', data: { code: 'propose', message: `Couldn’t propose a ${action.toLowerCase()} for ${d.name}.` } });
        } finally {
            this.proposing.set(null);
        }
    }
}
